import { createContext, useEffect, useContext, useRef, useLayoutEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { jsx } from 'react/jsx-runtime';

const ContextMenuContext = /*#__PURE__*/createContext({
  contextMenuEvent: null,
  registerMenu: () => {},
  registerMenuItem: () => {}
});

// Closes a modal dialog if the user clicks outside of it or types "Escape"
function useModalDismissSignal(modalRef, dismissCallback, dismissOnClickOutside = true) {
  useEffect(() => {
    const element = modalRef.current;
    if (element === null) {
      return;
    }
    const handleKeyboardEvent = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        dismissCallback();
      }
    };
    const handleMouseEvent = event => {
      if (event.defaultPrevented) {
        return;
      }
      if (!element.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        dismissCallback();
      }
    };
    let ownerDocument = null;

    // Delay until after the current call stack is empty,
    // in case this effect is being run while an event is currently bubbling.
    // In that case, we don't want to listen to the pre-existing event.
    let timeoutID = setTimeout(() => {
      timeoutID = null;

      // It's important to listen to the ownerDocument to support browser extensions.
      // The root document might belong to a different window.
      ownerDocument = element.ownerDocument;
      ownerDocument.addEventListener("keydown", handleKeyboardEvent);
      if (dismissOnClickOutside) {
        ownerDocument.addEventListener("click", handleMouseEvent, true);
        ownerDocument.addEventListener("contextmenu", handleMouseEvent, true);
        ownerDocument.addEventListener("mousedown", handleMouseEvent, true);
        ownerDocument.addEventListener("scroll", dismissCallback, true);
      }
    }, 0);
    return () => {
      if (timeoutID !== null) {
        clearTimeout(timeoutID);
      }
      if (ownerDocument !== null) {
        ownerDocument.removeEventListener("keydown", handleKeyboardEvent);
        ownerDocument.removeEventListener("click", handleMouseEvent, true);
        ownerDocument.removeEventListener("contextmenu", handleMouseEvent, true);
        ownerDocument.removeEventListener("mousedown", handleMouseEvent, true);
        ownerDocument.removeEventListener("scroll", dismissCallback, true);
      }
    };
  }, [modalRef, dismissCallback, dismissOnClickOutside]);
}

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function ContextMenu({
  alignTo,
  children,
  className,
  clientX,
  clientY,
  targetRect,
  dataTestId,
  dataTestName = "ContextMenu",
  hide,
  style: styleFromProps
}) {
  const {
    contextMenuEvent,
    registerMenu
  } = useContext(ContextMenuContext);
  let alignToTarget = alignTo === "auto-target" || contextMenuEvent?.type.startsWith("key");
  const ref = useRef(null);
  useLayoutEffect(() => {
    registerMenu(ref.current);
  }, [registerMenu]);
  const offsetsRef = useRef({
    x: 0,
    y: 0
  });
  useModalDismissSignal(ref, hide, true);

  // Optimally position the popup within the viewport
  useLayoutEffect(() => {
    const contextMenu = ref.current;
    const rect = contextMenu.getBoundingClientRect();
    const offsets = offsetsRef.current;
    if (rect.width < window.innerWidth) {
      if (rect.right > window.innerWidth) {
        if (rect.x - rect.width > 0) {
          offsets.x = 0 - rect.width;
        } else {
          offsets.x = 0 - rect.x;
        }
      }
    }
    if (rect.height < window.innerHeight) {
      if (rect.bottom > window.innerHeight) {
        if (alignToTarget) {
          if (rect.y - targetRect.height - rect.height > 0) {
            offsets.y = 0 - targetRect.height - rect.height;
          } else {
            offsets.y = 0 - rect.y;
          }
        } else {
          if (rect.y - rect.height > 0) {
            offsets.y = 0 - rect.height;
          } else {
            offsets.y = 0 - rect.y;
          }
        }
      }
    }
    if (alignToTarget) {
      contextMenu.style.left = `${targetRect.left + offsets.x}px`;
      contextMenu.style.top = `${targetRect.top + targetRect.height + offsets.y}px`;
    } else {
      contextMenu.style.left = `${clientX + offsets.x}px`;
      contextMenu.style.top = `${clientY + offsets.y}px`;
    }
  }, [alignToTarget, clientX, clientY, targetRect]);
  const onClick = event => {
    if (event.defaultPrevented) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    hide();
  };
  const onMouseMove = event => {
    event.stopPropagation();
  };
  const offsets = offsetsRef.current;
  let style;
  if (alignToTarget) {
    style = {
      left: targetRect.left + offsets.x,
      minWidth: targetRect.width,
      top: targetRect.top + targetRect.height + offsets.y
    };
  } else {
    style = {
      left: clientX + offsets.x,
      top: clientY + offsets.y
    };
  }
  if (styleFromProps) {
    style = Object.assign(style, styleFromProps);
  }
  return /*#__PURE__*/createPortal( /*#__PURE__*/jsx("div", {
    className: "useContextMenu_Backdrop",
    onClick: onClick,
    onMouseMove: onMouseMove,
    children: /*#__PURE__*/jsx("div", {
      className: classNames("useContextMenu_ContextMenu", className),
      "data-context-menu": true,
      "data-test-id": dataTestId,
      "data-test-name": dataTestName,
      ref: ref,
      style: style,
      tabIndex: 0,
      children: children
    })
  }), document.body);
}

function ContextMenuCategory({
  children,
  className,
  style
}) {
  const onClick = event => {
    event.preventDefault();
    event.stopPropagation();
  };
  return /*#__PURE__*/jsx("div", {
    className: classNames("useContextMenu_ContextMenuCategory", className),
    "data-context-menu-category": true,
    onClick: onClick,
    style: style,
    children: children
  });
}

function ContextMenuDivider({
  className,
  style
}) {
  const onClick = event => {
    event.preventDefault();
    event.stopPropagation();
  };
  return /*#__PURE__*/jsx("div", {
    className: classNames("useContextMenu_Divider", className),
    "data-context-menu-divider": true,
    onClick: onClick,
    style: style
  });
}

function ContextMenuItem({
  children,
  className,
  dataTestId,
  dataTestName = "ContextMenuItem",
  dataTestState,
  disabled = false,
  onSelect,
  style
}) {
  const {
    registerMenuItem
  } = useContext(ContextMenuContext);
  const ref = useRef(null);
  useLayoutEffect(() => {
    registerMenuItem(ref.current);
  }, [registerMenuItem]);
  const onClick = event => {
    if (event.defaultPrevented) {
      return;
    }
    if (!disabled) {
      if (onSelect) {
        onSelect(event);
      }
    }
  };
  const onKeyDown = event => {
    if (event.defaultPrevented) {
      return;
    }
    if (!disabled) {
      if (onSelect) {
        switch (event.key) {
          case "ArrowDown":
          case "ArrowUp":
          case "Enter":
          case " ":
            onSelect(event);
            break;
        }
      }
    }
  };
  return /*#__PURE__*/jsx("div", {
    className: classNames(disabled ? "useContextMenu_ContextMenuItemDisabled" : "useContextMenu_ContextMenuItem", className),
    "data-context-menu-item": true,
    "data-disabled": disabled,
    "data-test-id": dataTestId,
    "data-test-name": dataTestName,
    "data-test-state": dataTestState,
    onClick: onClick,
    onKeyDown: onKeyDown,
    ref: ref,
    style: style,
    tabIndex: disabled ? -1 : 0,
    children: children
  });
}

function useContextMenu(contextMenuItems, options = {}) {
  const {
    alignTo = "auto-cursor",
    className,
    dataTestId,
    dataTestName,
    onHide,
    onShow,
    requireClickToShow = false,
    style
  } = options;
  const [state, setState] = useState(null);
  const menuRef = useRef();
  const menuItemsRef = useRef([]);
  const registerMenu = useCallback(menu => {
    menuRef.current = menu;
  }, []);
  const registerMenuItem = useCallback(menuItem => {
    const menuItems = menuItemsRef.current;
    menuItems.push(menuItem);
  }, []);
  useEffect(() => {
    if (state == null) {
      return;
    }
    const target = state.event.target;
    const menu = menuRef.current;
    const menuItems = menuItemsRef.current;
    const enabledMenuItems = menuItems.reduce((reduced, menuItem, index) => {
      if (menuItem.getAttribute("data-disabled") !== "true") {
        reduced.push(index);
      }
      return reduced;
    }, []);
    const isMouseEvent = state.event.type === "contextmenu" || state.event.type === "click";
    let focusIndex = isMouseEvent ? -1 : 0;
    if (focusIndex >= 0) {
      menuItems[0].focus();
    } else {
      target.blur();
      menu.focus();
    }
    const focus = () => {
      const index = enabledMenuItems[focusIndex];
      const menuItem = menuItems[index];
      menuItem.focus();
    };
    const onKeyDown = event => {
      switch (event.key) {
        case "ArrowDown":
          {
            focusIndex = focusIndex + 1 < enabledMenuItems.length ? focusIndex + 1 : 0;
            focus();
            event.preventDefault();
            event.stopPropagation();
            break;
          }
        case "ArrowUp":
          {
            focusIndex = focusIndex - 1 >= 0 ? focusIndex - 1 : enabledMenuItems.length - 1;
            focus();
            event.preventDefault();
            event.stopPropagation();
            break;
          }
        case "Enter":
          {
            const index = enabledMenuItems[focusIndex];
            const menuItem = menuItems[index];
            menuItem.click();
            break;
          }
        case "Tab":
          {
            if (event.shiftKey) {
              focusIndex = focusIndex - 1 >= 0 ? focusIndex - 1 : enabledMenuItems.length - 1;
            } else {
              focusIndex = focusIndex + 1 < enabledMenuItems.length ? focusIndex + 1 : 0;
            }
            focus();
            event.preventDefault();
            event.stopPropagation();
            break;
          }
      }
    };
    menu.addEventListener("keydown", onKeyDown);
    return () => {
      menu.removeEventListener("keydown", onKeyDown);
      menuItems.splice(0, menuItems.length);

      // Return focus to the target element that triggered the context menu.
      target.focus();
    };
  }, [requireClickToShow, state]);
  const committedValuesRef = useRef({
    onHide,
    onShow,
    state
  });
  useEffect(() => {
    committedValuesRef.current.onHide = onHide;
    committedValuesRef.current.onShow = onShow;
    committedValuesRef.current.state = state;
  });
  const context = useMemo(() => ({
    contextMenuEvent: state?.event ?? null,
    registerMenu,
    registerMenuItem
  }), [registerMenu, registerMenuItem, state?.event]);
  const showMenu = event => {
    if (event.defaultPrevented) {
      // Support nested context menus
      return;
    }
    event.preventDefault();
    if (typeof onShow === "function") {
      onShow(event);
    }
    const {
      currentTarget
    } = event;
    const targetRect = currentTarget.getBoundingClientRect();
    const clientX = isMouseEvent$1(event) ? event.clientX : targetRect.x;
    const clientY = isMouseEvent$1(event) ? event.clientY : targetRect.y;
    setState({
      clientX,
      clientY,
      event,
      targetRect
    });
  };
  const onContextMenu = showMenu;
  const onKeyDown = event => {
    if (state !== null) {
      return;
    } else if (requireClickToShow) {
      return;
    }
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "ContextMenu":
      case "Enter":
      case " ":
        {
          showMenu(event);
          break;
        }
    }
  };
  const hideMenu = useCallback(() => {
    const {
      onHide,
      state
    } = committedValuesRef.current;
    if (state == null) {
      return;
    }
    setState(null);
    if (typeof onHide === "function") {
      onHide();
    }
  }, []);
  let contextMenu = null;
  if (state) {
    contextMenu = /*#__PURE__*/jsx(ContextMenuContext.Provider, {
      value: context,
      children: /*#__PURE__*/jsx(ContextMenu, {
        alignTo: alignTo,
        className: className,
        clientX: state.clientX,
        clientY: state.clientY,
        dataTestId: dataTestId,
        dataTestName: dataTestName,
        hide: hideMenu,
        style: style,
        targetRect: state.targetRect,
        children: contextMenuItems
      })
    });
  }
  return {
    contextMenu,
    hideMenu,
    onContextMenu,
    onKeyDown
  };
}
function isMouseEvent$1(event) {
  return event.pageX != null && event.pageY != null;
}

function isKeyboardEvent(event) {
  return event.type.startsWith("key");
}
function isMouseEvent(event) {
  return event.type === "click" || event.type === "contextmenu" || event.type.startsWith("mouse");
}

function assert(expectedCondition, message = "Assertion failed") {
  if (!expectedCondition) {
    console.error(message);
    throw Error(message);
  }
}
function assertKeyboardEvent(event, message = "KeyboardEvent expected") {
  if (isKeyboardEvent(event)) {
    return true;
  }
  console.error(message);
  throw Error(message);
}
function assertMouseEvent(event, message = "MouseEvent expected") {
  if (isMouseEvent(event)) {
    return true;
  }
  console.error(message);
  throw Error(message);
}

export { ContextMenu, ContextMenuCategory, ContextMenuDivider, ContextMenuItem, assert, assertKeyboardEvent, assertMouseEvent, isKeyboardEvent, isMouseEvent, useContextMenu, useModalDismissSignal };
